import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getPreferences, updatePreferences, MAX_CUSTOM_INSTRUCTIONS, type Preferences } from '@/services/preferences';
import { extractErrorMessage } from '@/services/api';
import { hydratePreferencesIntoChatStore } from '@/services/preference_sync';
import { applyTheme } from '@/hooks/useTheme';

interface SettingsPanelProps {
  onBack?: () => void;
}

export function SettingsPanel({ onBack }: SettingsPanelProps = {}) {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPreferences()
      .then((p) => setPrefs(p))
      .catch((e) => toast.error(extractErrorMessage(e, 'Could not load preferences')))
      .finally(() => setLoading(false));
  }, []);

  const patch = (p: Partial<Preferences>) => {
    setPrefs((prev) => (prev ? { ...prev, ...p } : prev));
  };

  const onSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const next = await updatePreferences(prefs);
      setPrefs(next);
      // Re-hydrate chatStore so ModelPicker reflects a changed default
      // without requiring a reload.
      await hydratePreferencesIntoChatStore();
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to chat"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-surface-hover dark:border-border-dark dark:bg-surface-dark dark:text-text-primary-dark dark:hover:bg-surface-hover-dark"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          )}
          <h2 className="text-sm font-semibold">Settings</h2>
        </div>
      </div>

      <Section title="Response style">
        <Select
          label="Language"
          value={prefs.language ?? 'english'}
          options={[
            { value: 'english', label: 'English' },
            { value: 'hinglish', label: 'Hinglish' },
            { value: 'hindi', label: 'Hindi' },
          ]}
          onChange={(v) => patch({ language: v as Preferences['language'] })}
        />
        <Select
          label="Rephrase language"
          value={prefs.rephrase_language ?? 'auto'}
          options={[
            { value: 'auto', label: 'Auto (match my input)' },
            { value: 'english', label: 'English' },
            { value: 'hinglish', label: 'Hinglish' },
            { value: 'hindi', label: 'Hindi' },
          ]}
          onChange={(v) => patch({ rephrase_language: v as Preferences['rephrase_language'] })}
        />
        <Select
          label="Verbosity"
          value={prefs.verbosity ?? 'balanced'}
          options={[
            { value: 'concise', label: 'Concise' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'detailed', label: 'Detailed' },
          ]}
          onChange={(v) => patch({ verbosity: v as Preferences['verbosity'] })}
        />
      </Section>

      <Section title="Custom instructions">
        <label className="space-y-1 text-xs">
          <span className="block font-medium text-text-secondary">
            Tell the assistant how to work with you
          </span>
          <textarea
            value={prefs.custom_instructions ?? ''}
            onChange={(e) => patch({ custom_instructions: e.target.value })}
            rows={6}
            maxLength={MAX_CUSTOM_INSTRUCTIONS}
            placeholder={'Example:\n- I work with monthly sales data\n- Always prefer XLOOKUP over VLOOKUP\n- Use Indian Rupees (₹) for currency\n- Explain formulas step by step'}
            className="w-full resize-y rounded-md border border-border bg-transparent p-2 text-xs outline-none focus:border-primary dark:border-border-dark"
          />
          <div className="flex justify-between text-[10px] text-text-secondary">
            <span>Applies to every new message. Saved across devices.</span>
            <span>
              {(prefs.custom_instructions ?? '').length}/{MAX_CUSTOM_INSTRUCTIONS}
            </span>
          </div>
        </label>
      </Section>

      <Section title="Appearance">
        <Select
          label="Theme"
          value={prefs.theme ?? 'system'}
          options={[
            { value: 'system', label: 'Match system' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
          onChange={(v) => {
            const t = v as NonNullable<Preferences['theme']>;
            patch({ theme: t });
            // Apply immediately so the picker-to-preview loop is tight.
            // Save button still persists the server-side preference.
            applyTheme(t);
          }}
        />
      </Section>

      <Section title="Actions">
        <label className="flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={prefs.auto_approve_safe_actions}
            onChange={(e) => patch({ auto_approve_safe_actions: e.target.checked })}
            className="mt-0.5 h-3.5 w-3.5 accent-primary"
          />
          <span>
            <span className="block font-medium">Auto-approve safe actions</span>
            <span className="text-text-secondary">
              When enabled, non-destructive changes skip the approval dialog. Destructive
              changes always require confirmation.
            </span>
          </span>
        </label>
      </Section>

      <button type="button" onClick={onSave} disabled={saving} className="btn-primary text-xs">
        {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
        Save
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card space-y-3 p-3">
      <div className="text-[11px] uppercase tracking-wide text-text-secondary">{title}</div>
      {children}
    </div>
  );
}

function Select({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="space-y-1 text-xs">
      <span className="block font-medium text-text-secondary">{label}</span>
      <select className="input py-1.5" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
